import React, { useEffect, useRef, useState, startTransition } from 'react'
import Sticky from 'react-stickynode'
import { useShopifyConfig } from '@/organisms/HorizontalShopifyMerchScroller/ShopifyConfigContext'
import { useWatchProjectContext } from '@/services/ProjectsService/WatchProjectProvider'
import { Project } from '@/services/ProjectsService/types'
import { Media } from '@/utils/EpisodeUtil'
import { isDefined } from '@/utils/primitives/generics'
import { useTranslate } from '@/utils/translate/translate-client'
import { PillTabItem, PillTabs } from '../PillTabs'
import { BonusContentTab } from './BonusContentTab'
import { DownloadsTab } from './DownloadsTab'
import { GiftsTab } from './GiftsTab/GiftsTab'
import { LivestreamsTab } from './LivestreamsTab'

interface ExtrasTabProps {
  hasBonusVideos?: boolean
  extraTabs: PillTabItem[]
  activeTabId: string
  setActiveTabId: (id: string) => void
  movieSpecialMedias: Media[]
  projectData: Project
  hasWatchableReasons: boolean
  isMovieOrSpecial: boolean
}

export const ExtrasTab: React.FC<ExtrasTabProps> = ({
  activeTabId,
  setActiveTabId,
  hasBonusVideos,
  extraTabs,
  movieSpecialMedias,
  projectData,
  hasWatchableReasons,
  isMovieOrSpecial,
}) => {
  const { t } = useTranslate('watch')
  const { bonusVideos, livestreams, downloadables, hasLivestreams, hasDownloadables, hasMerchItems } =
    useWatchProjectContext()
  const [stickyTop, setStickyTop] = useState(80)
  const containerRef = useRef<HTMLDivElement>(null)
  const { storeUrl, accessToken, handle, storeHref } = useShopifyConfig()
  const projectSlug = projectData?.slug || ''
  const projectName = projectData?.name || null

  useEffect(() => {
    if (typeof window === 'undefined') return
    const mq = window.matchMedia('(min-width: 1024px)')
    const update = () => {
      startTransition(() => {
        setStickyTop(mq.matches ? 112 : 80)
      })
    }
    update()
    mq.addEventListener('change', update)
    return () => mq.removeEventListener('change', update)
  }, [])

  useEffect(() => {
    if (containerRef.current && typeof window !== 'undefined') {
      const containerTop = containerRef.current.getBoundingClientRect().top + window.scrollY
      const currentScroll = window.scrollY
      const stickyOffset = stickyTop

      // If we're scrolled past the container (i.e. a really long livestreams list), scroll back to it
      if (currentScroll > containerTop) {
        window.scrollTo({
          top: containerTop - stickyOffset,
          behavior: 'smooth',
        })
      }
    }
  }, [activeTabId, stickyTop])

  return (
    <div ref={containerRef} className="extras-tab-container flex h-full w-full flex-col overflow-x-hidden lg:flex-row">
      <div className="relative lg:w-full lg:min-w-[396px] lg:max-w-fit xl:min-w-[428px]">
        <Sticky
          activeClass="z-[998] opacity-1 relative active"
          releasedClass="z-0 opacity-0"
          className="opacity-1 relative w-full transition-opacity duration-300 ease-in-out"
          top={stickyTop}
          bottomBoundary=".extras-tab-container"
        >
          <div className="bg-core-black pb-4 pt-2">
            <PillTabs
              items={extraTabs}
              activeId={activeTabId}
              onChange={(item) => setActiveTabId(item.id)}
              ariaLabel={t('extras_tabs_aria_label', 'Extras tabs')}
            />
          </div>
        </Sticky>
      </div>
      <div className="flex min-h-[300px] flex-col gap-y-10 pl-4 pr-3 md:gap-y-6 md:px-12 lg:min-h-[500px] lg:gap-y-10 lg:pr-16">
        {activeTabId === 'livestreams' && hasLivestreams && (
          <LivestreamsTab
            livestreamsMedias={livestreams?.filter(isDefined) ?? []}
            projectSlug={projectSlug}
            pagePlacement={isMovieOrSpecial ? 1 : 2}
          />
        )}
        {activeTabId === 'bonus' && hasBonusVideos && bonusVideos && (
          <BonusContentTab
            projectSlug={projectSlug}
            videos={bonusVideos}
            movieSpecialMedias={movieSpecialMedias}
            hasWatchableReasons={hasWatchableReasons}
            pagePlacement={isMovieOrSpecial ? 1 : 2}
          />
        )}
        {activeTabId === 'gifts' && hasMerchItems && storeUrl && storeHref && accessToken && handle && (
          <GiftsTab
            storeUrl={storeUrl}
            storeHref={storeHref}
            accessToken={accessToken}
            projectSlug={projectSlug}
            projectName={projectName}
            handle={handle}
            projectData={projectData}
            analytics={{
              program: projectSlug,
              context: 'watch-tab',
            }}
            utm={{
              linkContext: 'watch-tab',
              campaign: 'elevated-shopify-carousel',
            }}
          />
        )}
        {activeTabId === 'downloads' && hasDownloadables && (
          <DownloadsTab downloadables={downloadables} projectSlug={projectSlug} />
        )}
      </div>
    </div>
  )
}
